import axios from 'axios';
import { buildUrl, getAuthHeaders } from '../config/api';

// Create axios instance with default config
const apiClient = axios.create({
  timeout: 10000, // 10 seconds timeout
});

// Add request interceptor to automatically add auth headers
apiClient.interceptors.request.use(
  (config) => {
    // Add auth headers to every request
    const authHeaders = getAuthHeaders();
    config.headers = {
      ...config.headers,
      ...authHeaders
    };
    
    // Build full URL if endpoint starts with /api
    if (config.url && config.url.startsWith('/api')) {
      config.url = buildUrl(config.url);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('adminToken');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// API helper functions
export const api = {
  // Auth endpoints
  login: (credentials) => apiClient.post('/api/auth/login', credentials),
  register: (userData) => apiClient.post('/api/auth/register', userData),
  
  // Dashboard
  getDashboard: () => apiClient.get('/api/admin/dashboard'),
  
  // Users
  getUsers: (params) => apiClient.get('/api/admin/users', { params }),
  createUser: (userData) => apiClient.post('/api/admin/users', userData),
  updateUser: (id, userData) => apiClient.put(`/api/admin/users/${id}`, userData),
  deleteUser: (id) => apiClient.delete(`/api/admin/users/${id}`),
  updateUserBalance: (id, balanceData) => apiClient.put(`/api/admin/users/${id}/balance`, balanceData),
  addFunds: (id, fundsData) => apiClient.post(`/api/admin/users/${id}/funds`, fundsData),
  getUserPortfolio: (id) => apiClient.get(`/api/admin/users/${id}/portfolio`),
  getUserTransactions: (id, params) => apiClient.get(`/api/admin/users/${id}/transactions`, { params }),
  
  // Orders
  getOrders: (params) => apiClient.get('/api/admin/orders', { params }),
  updateOrderStatus: (id, status) => apiClient.put(`/api/admin/orders/${id}/status`, { status }),
  deleteOrder: (id) => apiClient.delete(`/api/admin/orders/${id}`),
  
  // Transactions
  getTransactions: (params) => apiClient.get('/api/admin/transactions', { params }),
  updateTransactionStatus: (id, data) => apiClient.put(`/api/admin/transactions/${id}/status`, data),
  bulkTransactionAction: (data) => apiClient.post('/api/admin/transactions/bulk-action', data),
  getTransactionAnalytics: (params) => apiClient.get('/api/admin/analytics/transactions', { params }),
  
  // Stocks
  getStocks: (params) => apiClient.get('/api/admin/stocks', { params }),
  createStock: (stockData) => apiClient.post('/api/admin/stocks', stockData),
  updateStock: (id, stockData) => apiClient.put(`/api/admin/stocks/${id}`, stockData),
  deleteStock: (id) => apiClient.delete(`/api/admin/stocks/${id}`),
  
  // Analytics
  getAnalytics: (params) => apiClient.get('/api/admin/analytics', { params }),
  
  // Config & Server Management
  getConfig: () => apiClient.get('/api/admin/config'),
  updateToken: (tokenData) => apiClient.post('/api/admin/config/update-token', tokenData),
  restartServer: (reason) => apiClient.post('/api/admin/restart', { reason }),
  getServerStatus: () => apiClient.get('/api/admin/server-status'),
};

// Generic API methods for backward compatibility
api.get = (url, config) => apiClient.get(url, config);
api.post = (url, data, config) => apiClient.post(url, data, config);
api.put = (url, data, config) => apiClient.put(url, data, config);
api.delete = (url, config) => apiClient.delete(url, config);

export default apiClient;
